/** @jsxImportSource preact */
import { useState } from 'preact/hooks';
import type { ListSection } from '../types';
import type { ActionHandler } from './types';
import { t } from '../i18n';

interface ListMessageProps {
  text?: string;
  button_label: string;
  sections: ListSection[];
  onAction: ActionHandler;
}

/**
 * WhatsApp "list message" (interactive list). Renders an optional body text
 * as an assistant bubble, then a list-button (☰ glyph + label). Tapping it
 * opens a bottom-sheet — a positioned overlay inside the widget window with
 * a dim backdrop — listing each section (title) and its rows (bold title +
 * muted description). Picking a row re-sends its `reply` as the shopper's
 * next message and closes the sheet.
 *
 * XSS: JSX text interpolation only. No dangerouslySetInnerHTML.
 */
export function ListMessage({ text, button_label, sections, onAction }: ListMessageProps) {
  const [open, setOpen] = useState(false);

  const safeSections = (Array.isArray(sections) ? sections : []).filter(
    (s) => s && Array.isArray(s.rows) && s.rows.length > 0,
  );

  const label = button_label || t('list.open');

  const close = () => setOpen(false);
  const pick = (reply: string) => {
    setOpen(false);
    onAction({ kind: 'send-reply', text: reply });
  };

  return (
    <div class="lc-message assistant lc-list">
      {text ? <div class="lc-message-bubble lc-list-text">{text}</div> : null}
      <button
        type="button"
        class="lc-list-button"
        onClick={() => setOpen(true)}
        disabled={safeSections.length === 0}
      >
        <span class="lc-list-button-glyph" aria-hidden="true">☰</span>
        <span class="lc-list-button-label">{label}</span>
      </button>

      {open ? (
        <div class="lc-list-sheet-root">
          <div
            class="lc-list-sheet-backdrop"
            onClick={close}
            aria-hidden="true"
          />
          <div
            class="lc-list-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={label}
          >
            <div class="lc-list-sheet-header">
              <span class="lc-list-sheet-title">{label}</span>
              <button
                type="button"
                class="lc-list-sheet-close"
                aria-label={t('list.close')}
                onClick={close}
              >
                ×
              </button>
            </div>
            <div class="lc-list-sheet-body">
              {safeSections.map((section, si) => (
                <div class="lc-list-section" key={`sec-${si}`}>
                  {section.title ? (
                    <div class="lc-list-section-title">{section.title}</div>
                  ) : null}
                  {section.rows
                    .filter((r) => r && typeof r.title === 'string')
                    .map((row, ri) => (
                      <button
                        type="button"
                        class="lc-list-row"
                        key={`row-${si}-${ri}`}
                        data-row-id={row.id || ''}
                        onClick={() => pick(row.reply)}
                      >
                        <span class="lc-list-row-title">{row.title}</span>
                        {row.description ? (
                          <span class="lc-list-row-desc">{row.description}</span>
                        ) : null}
                      </button>
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
